import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useBotCheck } from "./FreeFunctionsPage";
import {
  GS,
  SCANNER_THRESHOLD_MS,
  SCANNER_MIN_LENGTH,
  SCANNER_FINALIZE_DELAY_MS,
  normalizeCodeValue,
  analyzeKiz,
  useCameraScanner,
  RU_TO_LATIN_MAP,
  BARCODE_CAMERA_FORMATS,
  BARCODE_FORMATS,
} from "./chestnyZnakCore";
import { LABEL_SIZES, dataMatrixDataUrl, czLabelPreviewMeta, buildCzLabelHtml, openPrintWindow } from "./chestnyZnakPrint";
import "./BarcodeLabeler.css";
import "./ChestnyZnakLabeler.css";

// ─── date helpers ─────────────────────────────────────────────────────────────

const todayStr = () => new Date().toISOString().slice(0, 10);

function addDuration(dateStr, value, unit) {
  if (!dateStr || !value) return "";
  const n = parseInt(value);
  if (isNaN(n) || n <= 0) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  if (unit === "days")   d.setDate(d.getDate() + n);
  if (unit === "months") d.setMonth(d.getMonth() + n);
  if (unit === "years")  d.setFullYear(d.getFullYear() + n);
  return d.toISOString().slice(0, 10);
}

function diffAsDuration(d1Str, d2Str) {
  if (!d1Str || !d2Str) return null;
  const d1 = new Date(d1Str), d2 = new Date(d2Str);
  if (isNaN(d1) || isNaN(d2) || d2 <= d1) return null;
  const years = d2.getFullYear() - d1.getFullYear();
  if (years > 0) {
    const chk = new Date(d1); chk.setFullYear(chk.getFullYear() + years);
    if (chk.getTime() === d2.getTime()) return { value: String(years), unit: "years" };
  }
  const months = (d2.getFullYear() - d1.getFullYear()) * 12 + d2.getMonth() - d1.getMonth();
  if (months > 0) {
    const chk = new Date(d1); chk.setMonth(chk.getMonth() + months);
    if (chk.getTime() === d2.getTime()) return { value: String(months), unit: "months" };
  }
  return { value: String(Math.round((d2 - d1) / 86400000)), unit: "days" };
}

const shelfLabel = (value, unit) => {
  if (!value) return "";
  return `${value} ${{ days: "дн.", months: "мес.", years: "лет" }[unit] || unit}`;
};
const fmtDate = (s) => (s || "").split("-").reverse().join(".");

// ─── component ────────────────────────────────────────────────────────────────
// `settings` — конфигурация этикетки (формат, размер, доп. элементы), общая
// с режимом «Лента заказов»; хранится и меняется в родителе (ChestnyZnakTool).

// `prefill` — опциональная автоподстановка названия/штрихкода товара из внешнего
// источника (например, базы «Атрибуты» в разделе FBS). Передаётся как
// { title, barcodeValue, nonce } — при изменении `nonce` поля перезаписываются.
export default function ChestnyZnakLabeler({ settings, onSettingsChange, prefill }) {
  const [value,        setValue]        = useState("");
  const [title,        setTitle]        = useState("");
  const [scanWarning,  setScanWarning]  = useState("");
  const [barcodeError, setBarcodeError] = useState("");

  const [dateValues, setDateValues] = useState({
    mfg:   todayStr(),
    exp:   todayStr(),
    shelf: { value: "", unit: "months" },
  });
  const [dateManual, setDateManual] = useState({ mfg: true, exp: false, shelf: false });
  const [copied, setCopied] = useState("");

  const [barcodeValue, setBarcodeValue] = useState("");

  const { isBot, HoneypotField } = useBotCheck();

  // eslint-disable-next-line
  useEffect(() => {
    if (!prefill?.nonce) return;
    if (prefill.title) {
      setTitle(prefill.title);
      onSettingsChange({ titleEnabled: true });
    }
    if (prefill.barcodeValue) {
      setBarcodeValue(prefill.barcodeValue);
      onSettingsChange({ barcodeEnabled: true });
    }
  }, [prefill?.nonce]);

  const canvasRef     = useRef(null);
  const inputRef      = useRef(null);
  const barcodeInputRef = useRef(null);
  const previewBoxRef = useRef(null);
  const scannerBuffer = useRef([]);
  const scannerTimer  = useRef(null);
  const scannerFinalizeTimer = useRef(null);
  const pendingScanPrintRef = useRef(false);

  const font        = settings.font;
  const labelSize    = settings.labelSize;
  const rotated      = settings.rotated;
  const copies       = settings.copies;
  const autoPrint    = settings.autoPrint;
  const dateFields   = settings.dateFields;
  const titleEnabled = settings.titleEnabled;
  const barcodeEnabled = settings.barcodeEnabled;
  const barcodeFormat  = settings.barcodeFormat;

  const setFont        = useCallback((v) => onSettingsChange({ font: v }), [onSettingsChange]);
  const setLabelSize   = useCallback((v) => onSettingsChange({ labelSize: v }), [onSettingsChange]);
  const setRotated     = useCallback((v) => onSettingsChange({ rotated: typeof v === "function" ? v(settings.rotated) : v }), [onSettingsChange, settings.rotated]);
  const setCopies      = useCallback((v) => onSettingsChange({ copies: v }), [onSettingsChange]);
  const setAutoPrint   = useCallback((v) => onSettingsChange({ autoPrint: v }), [onSettingsChange]);
  const setTitleEnabled = useCallback((v) => onSettingsChange({ titleEnabled: v }), [onSettingsChange]);
  const setBarcodeEnabled = useCallback((v) => onSettingsChange({ barcodeEnabled: v }), [onSettingsChange]);
  const setBarcodeFormat  = useCallback((v) => onSettingsChange({ barcodeFormat: v }), [onSettingsChange]);

  // Measure actual preview box pixel width for accurate Data Matrix sizing
  const [previewBoxW, setPreviewBoxW] = useState(300);
  useEffect(() => {
    if (!previewBoxRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setPreviewBoxW(w);
    });
    ro.observe(previewBoxRef.current);
    return () => ro.disconnect();
  }, []);

  const handleScanResult = useCallback((text) => {
    pendingScanPrintRef.current = autoPrint;
    setValue(text);
    setScanWarning("");
    inputRef.current?.focus();
  }, [autoPrint]);

  const camera = useCameraScanner(handleScanResult);

  const handleBarcodeScanResult = useCallback((text) => {
    setBarcodeValue(text);
    barcodeInputRef.current?.focus();
  }, []);
  const barcodeCamera = useCameraScanner(handleBarcodeScanResult, BARCODE_CAMERA_FORMATS);

  // ─── преобразование КИЗ (Этап 2) ────────────────────────────────────────────
  const converted = useMemo(() => (value ? normalizeCodeValue(value) : ""), [value]);
  const convertedChanged = !!value && converted !== value;
  const gsCount = useMemo(
    () => (converted ? converted.split(GS).length - 1 : 0),
    [converted]
  );

  const copyConverted = useCallback((mode) => {
    if (!converted) return;
    const text = mode === "visible" ? converted.split(GS).join("<GS>") : converted;
    const done = () => { setCopied(mode); setTimeout(() => setCopied(""), 1600); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {});
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); done(); } catch {}
      document.body.removeChild(ta);
    }
  }, [converted]);

  // ─── доп. штрихкод: нормализация раскладки (USB-раскладка RU→EN) ───────────

  const barcodeConverted = useMemo(() => {
    if (!barcodeValue) return "";
    return /[а-яёА-ЯЁ]/.test(barcodeValue)
      ? barcodeValue.split("").map((c) => RU_TO_LATIN_MAP[c] ?? c).join("")
      : barcodeValue;
  }, [barcodeValue]);

  // ─── derived layout values ─────────────────────────────────────────────────

  const lw  = rotated ? parseFloat(labelSize.height) : parseFloat(labelSize.width);
  const lh  = rotated ? parseFloat(labelSize.width)  : parseFloat(labelSize.height);
  const effectiveW = rotated ? labelSize.height : labelSize.width;
  const effectiveH = rotated ? labelSize.width  : labelSize.height;

  const effectiveTitle = titleEnabled ? title : "";
  const effectiveBarcode = barcodeEnabled ? barcodeConverted : "";

  // Штрихкод рендерится внутри czLabelPreviewMeta — оттуда же берём готовую
  // картинку превью и ошибку формата, отдельно рендерить не нужно.
  const { hasText, showBarcode, layout, barcodeError: barcodeGenError } = useMemo(() => czLabelPreviewMeta({
    title: effectiveTitle, dateFields, dateValues, barcodeConverted: effectiveBarcode, barcodeFormat,
    labelW: effectiveW, labelH: effectiveH,
  }), [effectiveTitle, dateFields, dateValues, effectiveBarcode, barcodeFormat, effectiveW, effectiveH]);
  const barcodePreviewUrl = layout.barcodeDataUrl || "";

  const previewScale = previewBoxW / lw;
  const previewPadPx = Math.max(1, Math.round(0.5 * previewScale));
  const codePreviewPx = Math.max(20, Math.floor(Math.min(layout.codeW, layout.codeH) * previewScale));
  const previewTextFs = Math.max(5, Math.round(layout.fs * 1.33));
  const previewBarcodeStripH = Math.max(16, Math.round(layout.barcodeStripH * previewScale));
  const previewBarcodeStripW = Math.max(20, Math.round(layout.barcodeStripW * previewScale));
  const dmNoText = !hasText;
  const dmHoriz = hasText && layout.horiz;
  const dmPortrait = hasText && !layout.horiz;

  // ─── date formula ──────────────────────────────────────────────────────────

  useEffect(() => {
    setDateValues((prev) => {
      const next = { ...prev };
      if (dateFields.mfg && dateFields.shelf && dateFields.exp && !dateManual.exp) {
        const calc = addDuration(prev.mfg, prev.shelf.value, prev.shelf.unit);
        if (calc) next.exp = calc;
      } else if (dateFields.mfg && dateFields.exp && dateFields.shelf && !dateManual.shelf) {
        const diff = diffAsDuration(prev.mfg, prev.exp);
        if (diff) next.shelf = diff;
      }
      return next;
    });
  }, [dateValues.mfg, dateValues.exp, dateValues.shelf.value, dateValues.shelf.unit, dateFields, dateManual.exp, dateManual.shelf]);

  const dateInconsistent = (() => {
    if (!dateFields.mfg || !dateFields.exp || !dateFields.shelf) return false;
    if (!dateManual.mfg || !dateManual.exp || !dateManual.shelf) return false;
    const calc = addDuration(dateValues.mfg, dateValues.shelf.value, dateValues.shelf.unit);
    return !!(calc && calc !== dateValues.exp);
  })();

  const handleMfgChange   = (val) => { setDateManual((m) => ({ ...m, mfg: true }));              setDateValues((v) => ({ ...v, mfg: val })); };
  const handleExpChange   = (val) => { setDateManual((m) => ({ ...m, exp: true, shelf: false })); setDateValues((v) => ({ ...v, exp: val })); };
  const handleShelfValChange = (val) => { setDateManual((m) => ({ ...m, shelf: true, exp: false })); setDateValues((v) => ({ ...v, shelf: { ...v.shelf, value: val } })); };
  const handleShelfUnitChange = (unit) => { setDateManual((m) => ({ ...m, shelf: true, exp: false })); setDateValues((v) => ({ ...v, shelf: { ...v.shelf, unit } })); };
  const toggleDateField = (id) => {
    const next = { ...dateFields, [id]: !dateFields[id] };
    if (!dateFields[id]) setDateManual((m) => ({ ...m, [id]: false }));
    onSettingsChange({ dateFields: next });
  };

  // ─── Data Matrix preview ─────────────────────────────────────────────────────

  const renderBarcode = useCallback((val) => {
    if (!val) return;
    setBarcodeError("");
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const img = new Image();
      img.onload = () => {
        canvas.width = 256; canvas.height = 256;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, 256, 256);
        ctx.drawImage(img, 0, 0, 256, 256);
      };
      img.src = dataMatrixDataUrl(val, 256);
    } catch {
      setBarcodeError("Ошибка генерации Data Matrix ЧЗ");
    }
  }, []);

  useEffect(() => {
    if (value) renderBarcode(value);
  }, [value, font, labelSize, rotated, hasText, renderBarcode]);

  // ─── print ─────────────────────────────────────────────────────────────────

  const openLabelPrint = useCallback((forSave = false) => {
    if (!value) return;
    const html = buildCzLabelHtml({
      value,
      title: effectiveTitle,
      dateFields, dateValues,
      barcodeConverted: effectiveBarcode, barcodeFormat,
      font, labelW: effectiveW, labelH: effectiveH, copies,
    });
    openPrintWindow(html, forSave ? { title: `Этикетка_${value.replace(/\s/g, "_")}` } : {});
  }, [value, effectiveTitle, dateFields, dateValues, effectiveBarcode, barcodeFormat, font, effectiveW, effectiveH, copies]);

  const triggerPrint = useCallback(() => { if (!isBot()) openLabelPrint(false); }, [openLabelPrint, isBot]);
  const triggerSave  = useCallback(() => { if (!isBot()) openLabelPrint(true);  }, [openLabelPrint, isBot]);

  useEffect(() => {
    if (!autoPrint || !pendingScanPrintRef.current || !value.trim() || barcodeError) return undefined;
    pendingScanPrintRef.current = false;
    const t = setTimeout(() => triggerPrint(), 200);
    return () => clearTimeout(t);
  }, [autoPrint, value, barcodeError, triggerPrint]);

  // ─── USB/HID scanner + keyboard (поле «Значение» — ЧЗ) ─────────────────────

  const finalizeScannerBuffer = useCallback((shouldPrint = true) => {
    const buf = scannerBuffer.current.filter((ch) => typeof ch === "string");
    scannerBuffer.current = [];
    clearTimeout(scannerTimer.current);
    clearTimeout(scannerFinalizeTimer.current);
    scannerFinalizeTimer.current = null;

    if (buf.length < SCANNER_MIN_LENGTH) return false;

    const scanned = buf.join("");
    // Защита «от дурака»: двойное/неполное сканирование или случайная клавиша
    // ломают структуру КИЗ. Проверяем ПРЕОБРАЗОВАННОЕ значение (после разбора
    // HRI-скобочного формата «(01)...(21)...»), а не сырой ввод — иначе валидные
    // коды, которые сканер шлёт без настоящих GS-разделителей, ложно бракуются.
    const { structureOk } = analyzeKiz(normalizeCodeValue(scanned));
    setScanWarning(structureOk ? "" : "Код не прошёл проверку структуры КИЗ (GTIN, серийный номер, ключ 91/92) — похоже на неполное или двойное сканирование. Проверьте и отсканируйте заново.");
    pendingScanPrintRef.current = autoPrint && shouldPrint && structureOk;
    setValue(scanned);
    return true;
  }, [autoPrint]);

  const handleKeyDown = useCallback((e) => {
    const isGsChar =
      e.keyCode === 29 ||
      e.key === "GroupSeparator" ||
      e.key === "\x1d" ||
      e.key === "F8" ||
      (e.ctrlKey && e.key === "]" && scannerBuffer.current.length > 0);
    if (isGsChar) {
      e.preventDefault();
      const now = Date.now();
      const last = scannerBuffer.current._lastTime || 0;
      if (now - last < SCANNER_THRESHOLD_MS || scannerBuffer.current.length > 0) {
        scannerBuffer.current.push(GS);
        scannerBuffer.current._lastTime = now;
        clearTimeout(scannerTimer.current);
        scannerTimer.current = setTimeout(() => { scannerBuffer.current = []; }, 500);
      }
      return;
    }

    if (e.key === "Enter" || e.key === "Tab") {
      const buf = scannerBuffer.current;
      e.preventDefault();
      if (buf.length >= SCANNER_MIN_LENGTH) {
        clearTimeout(scannerTimer.current);
        clearTimeout(scannerFinalizeTimer.current);
        scannerFinalizeTimer.current = setTimeout(() => {
          finalizeScannerBuffer(true);
        }, SCANNER_FINALIZE_DELAY_MS);
      } else {
        scannerBuffer.current = [];
        if (e.key === "Enter" && value.trim()) triggerPrint();
      }
      return;
    }
    if (e.key.length === 1) {
      const now  = Date.now();
      const last = scannerBuffer.current._lastTime || 0;
      let continuedAfterSeparator = false;
      if (scannerFinalizeTimer.current && scannerBuffer.current.length >= SCANNER_MIN_LENGTH) {
        clearTimeout(scannerFinalizeTimer.current);
        scannerFinalizeTimer.current = null;
        scannerBuffer.current.push(GS);
        continuedAfterSeparator = true;
      }
      if (continuedAfterSeparator || now - last < SCANNER_THRESHOLD_MS || scannerBuffer.current.length === 0) {
        scannerBuffer.current.push(e.key);
      } else {
        scannerBuffer.current = [e.key];
      }
      scannerBuffer.current._lastTime = now;
      clearTimeout(scannerTimer.current);
      scannerTimer.current = setTimeout(() => { scannerBuffer.current = []; }, 500);
    }
  }, [triggerPrint, value, finalizeScannerBuffer]);

  const clearValue = useCallback(() => {
    setValue("");
    setScanWarning("");
    scannerBuffer.current = [];
    inputRef.current?.focus();
  }, []);

  const clearAll = () => {
    setValue("");
    setTitle("");
    setBarcodeValue("");
    setScanWarning("");
    setBarcodeError("");
    inputRef.current?.focus();
  };

  const canAct = value.trim().length > 0 && !barcodeError && !barcodeGenError;

  // ─── preview date rows (reused in multiple layouts) ────────────────────────

  const PreviewDates = () => (
    <>
      {dateFields.mfg && dateValues.mfg && (
        <div className="bl__preview-date">
          <span className="bl__preview-date-key">Дата производства</span>
          <span className="bl__preview-date-val">{fmtDate(dateValues.mfg)}</span>
        </div>
      )}
      {dateFields.shelf && dateValues.shelf.value && (
        <div className="bl__preview-date">
          <span className="bl__preview-date-key">Срок годности</span>
          <span className="bl__preview-date-val">{shelfLabel(dateValues.shelf.value, dateValues.shelf.unit)}</span>
        </div>
      )}
      {dateFields.exp && dateValues.exp && (
        <div className="bl__preview-date">
          <span className="bl__preview-date-key">Годен до</span>
          <span className="bl__preview-date-val">{fmtDate(dateValues.exp)}</span>
        </div>
      )}
    </>
  );

  // ─── render ────────────────────────────────────────────────────────────────

  return (
    <div className="bl">

      {/* ── camera overlay: ЧЗ ── */}
      {camera.open && (
        <div className="bl__cam-overlay" onClick={camera.stopScan}>
          <div className="bl__cam-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bl__cam-header">
              <span>Наведите камеру на Data Matrix Честного знака</span>
              <div className="bl__cam-actions">
                {camera.devices.length > 1 && (
                  <button
                    className="bl__cam-switch"
                    onClick={camera.switchCamera}
                    type="button"
                    title="Сменить камеру"
                    aria-label="Сменить камеру"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 0 1-15.36 6.36L3 15"/>
                      <path d="M3 21v-6h6"/>
                      <path d="M3 12a9 9 0 0 1 15.36-6.36L21 9"/>
                      <path d="M21 3v6h-6"/>
                    </svg>
                  </button>
                )}
                <button className="bl__cam-close" onClick={camera.stopScan} aria-label="Закрыть">✕</button>
              </div>
            </div>
            <div className="bl__cam-viewport bl__cam-viewport--matrix">
              <video ref={camera.videoRef} className="bl__cam-video" autoPlay playsInline muted />
              <div className="bl__cam-crosshair" />
              {camera.scanned && (
                <div className="bl__cam-result">
                  <span className="bl__cam-check">✓</span> {camera.scanned}
                </div>
              )}
            </div>
            {camera.error && <div className="bl__cam-error">{camera.error}</div>}
            <div className="bl__cam-hint">Нажмите за пределами окна, чтобы закрыть</div>
          </div>
        </div>
      )}

      {/* ── camera overlay: доп. штрихкод ── */}
      {barcodeCamera.open && (
        <div className="bl__cam-overlay" onClick={barcodeCamera.stopScan}>
          <div className="bl__cam-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bl__cam-header">
              <span>Наведите камеру на штрихкод</span>
              <div className="bl__cam-actions">
                {barcodeCamera.devices.length > 1 && (
                  <button className="bl__cam-switch" onClick={barcodeCamera.switchCamera}
                    type="button" title="Сменить камеру" aria-label="Сменить камеру">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 0 1-15.36 6.36L3 15"/>
                      <path d="M3 21v-6h6"/>
                      <path d="M3 12a9 9 0 0 1 15.36-6.36L21 9"/>
                      <path d="M21 3v6h-6"/>
                    </svg>
                  </button>
                )}
                <button className="bl__cam-close" onClick={barcodeCamera.stopScan} aria-label="Закрыть">✕</button>
              </div>
            </div>
            <div className="bl__cam-viewport">
              <video ref={barcodeCamera.videoRef} className="bl__cam-video" autoPlay playsInline muted />
              <div className="bl__cam-crosshair" />
              {barcodeCamera.scanned && (
                <div className="bl__cam-result">
                  <span className="bl__cam-check">✓</span> {barcodeCamera.scanned}
                </div>
              )}
            </div>
            {barcodeCamera.error && <div className="bl__cam-error">{barcodeCamera.error}</div>}
            <div className="bl__cam-hint">Нажмите за пределами окна, чтобы закрыть</div>
          </div>
        </div>
      )}

      {/* ── controls ── */}
      <div className="bl__controls">

        {/* Format — locked to Data Matrix ЧЗ */}
        <div className="bl__row bl__row--col">
          <label className="bl__label">Формат кода</label>
          <div className="cz__fmt-locked">
            <span className="bl__fmt-tag">2D</span>Data Matrix (Честный знак / GS1)
          </div>
        </div>

        {/* Label size + rotate + copies */}
        <div className="bl__row" style={{ gap: 12 }}>
          <div className="bl__row bl__row--col" style={{ flex: 1 }}>
            <label className="bl__label">Этикетка</label>
            <div className="bl__size-row">
              <select className="bl__select" value={labelSize.id}
                onChange={(e) => { setLabelSize(LABEL_SIZES.find((s) => s.id === e.target.value)); setRotated(false); }}>
                {LABEL_SIZES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <button
                className={`bl__rotate-btn${rotated ? " bl__rotate-btn--on" : ""}`}
                onClick={() => setRotated((v) => !v)}
                title={rotated ? "Альбомная → книжная" : "Книжная → альбомная"}
                type="button"
                aria-label="Повернуть этикетку"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 4v6h6"/>
                  <path d="M3.51 15a9 9 0 1 0 .49-4.25"/>
                </svg>
              </button>
            </div>
          </div>
          <div className="bl__row bl__row--col">
            <label className="bl__label">Копий</label>
            <input className="bl__input bl__input--copies" type="number" min={1} max={100} value={copies}
              onChange={(e) => setCopies(Math.max(1, parseInt(e.target.value) || 1))} />
          </div>
        </div>

        {/* Font */}
        <div className="bl__row bl__row--col">
          <label className="bl__label">Шрифт текста</label>
          <select className="bl__select" value={font}
            onChange={(e) => setFont(e.target.value)} style={{ fontFamily: font }}>
            <option value="Arial" style={{ fontFamily: "Arial" }}>Arial</option>
            <option value="Verdana" style={{ fontFamily: "Verdana" }}>Verdana</option>
            <option value="Trebuchet MS" style={{ fontFamily: "Trebuchet MS" }}>Trebuchet MS</option>
            <option value="Georgia" style={{ fontFamily: "Georgia" }}>Georgia</option>
            <option value="Times New Roman" style={{ fontFamily: "Times New Roman" }}>Times New Roman</option>
            <option value="Courier New" style={{ fontFamily: "Courier New" }}>Courier New</option>
          </select>
        </div>

        {/* Value */}
        <div className="bl__row bl__row--col">
          <label className="bl__label">Значение</label>
          <div className="bl__input-wrap cz__input-wrap--clearable">
            <input
              ref={inputRef}
              className="bl__input bl__input--value"
              type="text"
              placeholder="Введите или поднесите сканер..."
              value={value}
              onChange={(e) => { setValue(e.target.value); setScanWarning(""); }}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            {value && (
              <button className="bl__input-clear" onClick={clearValue}
                type="button" title="Очистить" aria-label="Очистить поле">✕</button>
            )}
            <button className="bl__cam-btn" onClick={camera.startScan}
              title="Сканировать через камеру" type="button" aria-label="Открыть сканер камеры">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
            </button>
          </div>
          {scanWarning && <div className="cz__scan-warning">{scanWarning}</div>}
        </div>

        {/* Converted (Этап 2) */}
        <div className="bl__row bl__row--col">
          <label className="bl__label">
            Преобразованное
            <span className="bl__optional"> (корректный КИЗ для других программ)</span>
          </label>
          <div className="cz__converted">
            {converted ? (
              <div className="cz__converted-text" aria-label="Преобразованный код маркировки">
                {converted.split(GS).map((part, i, arr) => (
                  <React.Fragment key={i}>
                    <span className="cz__converted-part">{part}</span>
                    {i < arr.length - 1 && <span className="cz__gs" title="GS-разделитель (ASCII 29)">GS</span>}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className="cz__converted-empty">Отсканируйте или введите код маркировки</div>
            )}

            {converted && (
              <div className="cz__converted-actions">
                <button
                  type="button"
                  className="cz__copy-btn"
                  onClick={() => copyConverted("raw")}
                  title="Скопировать со скрытыми GS-разделителями (для вставки в другие программы)"
                >
                  {copied === "raw" ? "Скопировано ✓" : "Копировать"}
                </button>
                <button
                  type="button"
                  className="cz__copy-btn cz__copy-btn--ghost"
                  onClick={() => copyConverted("visible")}
                  title="Скопировать с текстовыми метками <GS> вместо скрытых разделителей"
                >
                  {copied === "visible" ? "Скопировано ✓" : "Копировать с <GS>"}
                </button>
              </div>
            )}
          </div>
          {converted && (
            <div className="cz__converted-note">
              {gsCount > 0
                ? `GS-разделителей: ${gsCount}. Скрытые разделители на месте — сканер настроен верно.`
                : "GS-разделители не найдены. Проверьте настройку сканера (режим GS1, передача GS как F8 или Alt+0029, английская раскладка)."}
              {convertedChanged && " Код был исправлен при преобразовании."}
            </div>
          )}
        </div>

        {/* Дополнительно на этикетке */}
        <div className="bl__date-section">
          <div className="bl__date-header">Дополнительно на этикетке</div>

          <div className="bl__date-row">
            <label className="bl__checkbox-label">
              <input type="checkbox" checked={titleEnabled} onChange={() => setTitleEnabled(!titleEnabled)} />
              Название
            </label>
            {titleEnabled && (
              <input className="bl__input" type="text" placeholder="Название товара..."
                value={title} onChange={(e) => setTitle(e.target.value)} style={{ fontFamily: font }} />
            )}
          </div>

          <div className="bl__date-row">
            <label className="bl__checkbox-label">
              <input type="checkbox" checked={dateFields.mfg} onChange={() => toggleDateField("mfg")} />
              Дата производства
            </label>
            {dateFields.mfg && (
              <input className="bl__input bl__input--date" type="date"
                value={dateValues.mfg} onChange={(e) => handleMfgChange(e.target.value)} />
            )}
          </div>

          <div className="bl__date-row">
            <label className="bl__checkbox-label">
              <input type="checkbox" checked={dateFields.shelf} onChange={() => toggleDateField("shelf")} />
              Срок годности
              {dateFields.shelf && !dateManual.shelf && (dateFields.mfg || dateFields.exp) && (
                <span className="bl__auto-tag">авто</span>
              )}
            </label>
            {dateFields.shelf && (
              <div className="bl__shelf-row">
                <input
                  className={`bl__input bl__input--shelf-num${!dateManual.shelf ? " bl__input--auto" : ""}`}
                  type="number" min={1} placeholder="число"
                  value={dateValues.shelf.value}
                  onChange={(e) => handleShelfValChange(e.target.value)}
                />
                <select className="bl__select bl__select--shelf"
                  value={dateValues.shelf.unit} onChange={(e) => handleShelfUnitChange(e.target.value)}>
                  <option value="days">дней</option>
                  <option value="months">месяцев</option>
                  <option value="years">лет</option>
                </select>
              </div>
            )}
          </div>

          <div className="bl__date-row">
            <label className="bl__checkbox-label">
              <input type="checkbox" checked={dateFields.exp} onChange={() => toggleDateField("exp")} />
              Годен до
              {dateFields.exp && !dateManual.exp && (dateFields.mfg || dateFields.shelf) && (
                <span className="bl__auto-tag">авто</span>
              )}
            </label>
            {dateFields.exp && (
              <input
                className={`bl__input bl__input--date${!dateManual.exp ? " bl__input--auto" : ""}`}
                type="date" value={dateValues.exp}
                onChange={(e) => handleExpChange(e.target.value)} />
            )}
          </div>

          {dateInconsistent && (
            <div className="bl__date-warning">
              Даты не сходятся: {fmtDate(dateValues.mfg)} + {shelfLabel(dateValues.shelf.value, dateValues.shelf.unit)} ≠ {fmtDate(dateValues.exp)}
            </div>
          )}

          <div className="bl__date-row bl__date-row--barcode">
            <label className="bl__checkbox-label">
              <input type="checkbox" checked={barcodeEnabled} onChange={() => setBarcodeEnabled(!barcodeEnabled)} />
              Штрихкод
            </label>
          </div>
          {barcodeEnabled && (
            <div className="cz__barcode-block">
              <select className="bl__select cz__barcode-fmt" value={barcodeFormat}
                onChange={(e) => setBarcodeFormat(e.target.value)}>
                {BARCODE_FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
              <div className="bl__input-wrap cz__input-wrap--clearable">
                <input
                  ref={barcodeInputRef}
                  className="bl__input bl__input--value"
                  type="text"
                  placeholder="Введите или поднесите сканер..."
                  value={barcodeValue}
                  onChange={(e) => setBarcodeValue(e.target.value)}
                />
                {barcodeValue && (
                  <button className="bl__input-clear" onClick={() => { setBarcodeValue(""); barcodeInputRef.current?.focus(); }}
                    type="button" title="Очистить" aria-label="Очистить поле">✕</button>
                )}
                <button className="bl__cam-btn" onClick={barcodeCamera.startScan}
                  title="Сканировать через камеру" type="button" aria-label="Открыть сканер камеры">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                </button>
              </div>
              {barcodeGenError && <div className="cz__scan-warning">{barcodeGenError}</div>}
            </div>
          )}
        </div>

        <HoneypotField />
        {barcodeError && <div className="bl__error">{barcodeError}</div>}

        <label className="bl__checkbox-label bl__autoprint-toggle">
          <input
            type="checkbox"
            checked={autoPrint}
            onChange={(e) => setAutoPrint(e.target.checked)}
          />
          Включить автопечать после сканирования
        </label>

        <div className="bl__actions">
          <button className="bl__print-btn" onClick={triggerPrint} disabled={!canAct}>
            Печать{copies > 1 ? ` (${copies} шт.)` : ""}
          </button>
          <button className="bl__save-btn" onClick={triggerSave} disabled={!canAct}
            title="В диалоге выберите «Сохранить как PDF»">
            Сохранить PDF
          </button>
          <button className="bl__clear-btn" onClick={clearAll}>
            ✕
          </button>
        </div>
        <div className="bl__hint">Enter — печать сразу. Сканер — авто-печать при считывании.</div>
      </div>

      {/* ── preview ── */}
      <div className="bl__preview">
        <div className="bl__preview-label">Превью этикетки</div>
        <div
          ref={previewBoxRef}
          className="bl__preview-box"
          style={{ aspectRatio: `${lw} / ${lh}` }}
        >
          {!value && <span className="bl__preview-hint">Введите значение</span>}

          {value && (
            <>
              {/* ── 1: Data Matrix без текста — центр ── */}
              {dmNoText && (
                <div className="bl__preview-inner bl__preview-inner--center" style={{ padding: previewPadPx }}>
                  <div className="bl__qr-square" style={{ width: codePreviewPx, height: codePreviewPx }}>
                    <canvas ref={canvasRef} width={256} height={256} />
                  </div>
                </div>
              )}

              {/* ── 2: Data Matrix + текст, landscape — код влево ── */}
              {dmHoriz && (
                <div
                  className="bl__preview-inner bl__preview-inner--horiz"
                  style={{
                    fontFamily: font,
                    padding: previewPadPx,
                    display: "grid",
                    gridTemplateColumns: `${layout.codeW}fr ${layout.textW}fr`,
                    gridTemplateRows: "1fr",
                    gap: 0,
                  }}
                >
                  <div className="bl__preview-code-cell">
                    <div className="bl__qr-square" style={{ width: codePreviewPx, height: codePreviewPx }}>
                      <canvas ref={canvasRef} width={256} height={256} />
                    </div>
                  </div>
                  <div className="bl__preview-text" style={{ fontSize: previewTextFs }}>
                    {effectiveTitle && <div className="bl__preview-title" style={{ fontSize: Math.round(previewTextFs * 1.08) }}>{effectiveTitle}</div>}
                    <PreviewDates />
                  </div>
                </div>
              )}

              {/* ── 3: Data Matrix + текст, portrait — код сверху ── */}
              {dmPortrait && (
                <div
                  className="bl__preview-inner bl__preview-inner--portrait"
                  style={{
                    fontFamily: font,
                    padding: previewPadPx,
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gridTemplateRows: `${layout.codeH}fr ${layout.textH}fr`,
                    gap: 0,
                  }}
                >
                  <div className="bl__preview-code-cell">
                    <div className="bl__qr-square" style={{ width: codePreviewPx, height: codePreviewPx }}>
                      <canvas ref={canvasRef} width={256} height={256} />
                    </div>
                  </div>
                  <div className="bl__preview-text bl__preview-text--below" style={{ fontSize: previewTextFs }}>
                    {effectiveTitle && <div className="bl__preview-title" style={{ fontSize: Math.round(previewTextFs * 1.08) }}>{effectiveTitle}</div>}
                    <PreviewDates />
                  </div>
                </div>
              )}

              {/* ── штрихкод: отдельная полоса на 90% ширины, снизу ── */}
              {showBarcode && barcodePreviewUrl && (
                <div className="bl__preview-barcode-strip" style={{ height: previewBarcodeStripH, width: previewBarcodeStripW }}>
                  <img src={barcodePreviewUrl} alt="" />
                </div>
              )}
            </>
          )}
        </div>
        <div className="bl__preview-size">{labelSize.label}</div>
      </div>
    </div>
  );
}
